// The session IS the channel. A Playground session is a portable JSON
// artifact: anyone with the file can resume the conversation, see the
// full history, and contribute new turns. Channels are the substrate;
// keys stay with the user.
//
// This is the Claude-Tag model translated to a no-server world:
//   - Channel members all see the same thread (Yes — by sharing the file)
//   - Memory is scoped to the channel (Yes — the channel is a self-contained log)
//   - Creds attached to the channel, not the user — DEFERRED to v0.1.
//     For now, each participant brings their own BYOK key. The session
//     declares which providers it needs (`requires`); the receiver supplies
//     their own. v0.1 will add optional channel-owned encrypted creds for
//     owner-pays-for-team scenarios. Sealed-box scheme: libsodium with a
//     passphrase the owner shares out-of-band.
//
// Format version stamped so future migrations have a hook. Schema is
// intentionally tiny — easy to read, easy to diff, easy to git.

export const SESSION_FORMAT_VERSION = 1;

export type Role = "user" | "assistant" | "system";

export interface SessionMessage {
  id: string;
  role: Role;
  content: string;
  /** ISO-8601. */
  created_at: string;
  /** Display name of whoever authored a user turn. */
  author?: string;
  /** Which BabyAI alternative was picked, if this turn was a Mosh Pit. */
  picked_provider?: string;
  /** Model that produced an assistant turn — for the log. */
  model?: string;
  /** For assistant turns produced by the agent loop: chronological
   *  log of every tool call+result that happened before the final
   *  text. Rendered inline below the message body. */
  tool_rounds?: ToolRound[];
  /** Per-emoji set of user display names who reacted. e.g.
   *  { "👍": ["alice", "bob"], "❓": ["claire"] }. Lightweight team
   *  signal — agree, disagree, confused — without spawning a thread
   *  for every small reaction. Syncs through the channel like any
   *  other message field. */
  reactions?: Record<string, string[]>;
}

export interface ToolRound {
  name: string;
  args: unknown;
  ok: boolean;
  detail: string;
}

export type ProviderPreference = "babyai" | "anthropic" | "auto";

/** Cap on document_history depth — old snapshots fall off the end.
 *  10 is enough for "I want my doc back from a few rewrites ago"
 *  without bloating the synced session JSON. */
export const DOCUMENT_HISTORY_MAX = 10;

/** Max bytes for a single document attached to a channel.
 *  ~25K tokens — already a hefty chunk of every-turn input. */
export const ATTACHED_DOCUMENT_MAX_BYTES = 100 * 1024;

/** Max bytes summed across all docs attached to one channel.
 *  300KB total / 75K tokens of every-turn input is the ceiling
 *  before the synced JSON gets uncomfortable. */
export const ATTACHED_DOCUMENTS_TOTAL_MAX_BYTES = 300 * 1024;

export interface AttachedDocument {
  /** Stable id, used by the UI to detach. Generated at attach time;
   *  not the same as the library doc's id (a doc can be detached and
   *  re-attached, possibly after the library doc was deleted). */
  id: string;
  /** Filename or rename label from the library at attach time. */
  name: string;
  /** Full text content — copied in from the library at attach time
   *  so multiplayer teammates see the same context. */
  content: string;
  bytes: number;
  attached_at: string;
  /** Display name of the user who attached it. */
  attached_by: string;
}

export interface Session {
  format_version: number;
  id: string;
  title: string;
  /** ISO-8601. */
  created_at: string;
  /** ISO-8601 — bumped on every append. */
  updated_at: string;
  /** Display name of the channel creator. */
  created_by: string;
  /** Which providers participants need keys for. */
  requires: Provider[];
  /** Default provider for new turns. `auto` = let BabyAI Mosh Pit pick. */
  provider_preference: ProviderPreference;
  /** Mosh Pit produces 3 candidates per BabyAI turn. Disabled when
   *  tools_enabled is on — agent loops need a single deterministic
   *  trajectory, not three forking ones. */
  mosh_pit_enabled: boolean;
  /** When true, the agent loop runs with the Playground tool registry
   *  (fetch_url, fetch_github_file, current_time, …). Single-model,
   *  multi-turn, non-streaming. */
  tools_enabled?: boolean;
  /** Channel mode. "chat" is the default — multi-user chat with optional
   *  AI agent. "brainstorm" turns on the Document lens: every Ask updates
   *  a shared markdown whiteboard the AI keeps organized, and the chat
   *  thread is the running conversation alongside. */
  mode?: "chat" | "brainstorm";
  /** Markdown whiteboard used by brainstorm mode. The AI rewrites it
   *  in full each Ask; users can edit it manually too. Syncs through
   *  the channel like any other session field. */
  document?: string;
  /** Rolling history of prior whiteboard snapshots, newest-first.
   *  Capped at DOCUMENT_HISTORY_MAX. Pushed every time `document` is
   *  replaced; popped by the Undo button. Remote-sync `replaceChannel`
   *  doesn't push — only direct edits + AI rewrites do. */
  document_history?: string[];
  /** Brainstorm Mosh-Pit: when true, each Ask in brainstorm mode
   *  fires 3 parallel rewrites with different organizing strategies
   *  (by theme / by contributor / by maturity) and presents them as
   *  tabs for the team to pick. Distinct from `mosh_pit_enabled`
   *  (which controls chat Mosh Pit) because the cost profile is
   *  different — 3 brainstorm rewrites at 2K tokens each is a lot
   *  more than 3 chat candidates at ~256 tokens each. Default off. */
  brainstorm_mp_enabled?: boolean;
  /** Running token totals over the lifetime of this channel.
   *  Bumped per AI dispatch that returns a usage block; `turns`
   *  counts dispatches regardless of whether usage was reported.
   *  Surfaced as the toolbar's cost meter so users see when a
   *  brainstorm is getting expensive. */
  total_tokens?: {
    prompt: number;
    completion: number;
    turns: number;
  };
  /** Pinned channels stay at the top of the sidebar, above the
   *  activity-sorted unpinned ones. Local-only intent (per-browser),
   *  but stored on the channel so multi-tab same-user sees the same
   *  pinning. Doesn't sync via GitHub because pinning is personal
   *  navigation state, not channel content. */
  pinned?: boolean;
  /** Archived channels are hidden from the sidebar by default to
   *  keep active rotation scannable. Toggle "show archived" in the
   *  sidebar to see and unarchive them. Same per-browser navigation-
   *  state caveat as pinning. Different from delete: archive
   *  preserves everything; delete drops the channel. */
  archived?: boolean;
  /** Documents attached to this channel for AI context. The doc
   *  content is copied in at attach time so multiplayer teammates
   *  see the same context the AI sees (rather than referencing a
   *  doc id from someone else's browser-local library). Each
   *  attached doc gets its full content concatenated into the system
   *  prompt on every AI call — cap enforced at attach time. */
  attached_documents?: AttachedDocument[];
  messages: SessionMessage[];
  /** Optional: provider keys encrypted with a passphrase the channel
   *  owner shares out-of-band. When present, joiners unlock the keys
   *  with the passphrase and use them for chat sends — "owner pays
   *  for the team." Forward compat: older clients without an unlock
   *  flow ignore this and fall back to per-user BYOK. */
  encrypted_keys?: EncryptedKeysBlob;
  /** Optional: GitHub Contents API location for shared-substrate sync.
   *  When present, browsers with a PAT poll this file every ~10s and
   *  push their own appends. The `last_sha` lets each client do
   *  optimistic-locking PUTs and detect upstream changes. */
  sync_target?: {
    owner: string;
    repo: string;
    path: string;
    last_sha?: string;
  };
}

export type Provider = "babyai" | "anthropic";

/** Encrypted bundle of provider keys attached to a channel.
 *  Shape lives in lib/channel-crypto.ts; re-declared here so Session
 *  stays self-describing without forcing crypto imports. */
export interface EncryptedKeysBlob {
  salt: string;
  iv: string;
  ciphertext: string;
  providers: Provider[];
}

export function newSession(title: string, createdBy: string): Session {
  const now = new Date().toISOString();
  return {
    format_version: SESSION_FORMAT_VERSION,
    id: crypto.randomUUID(),
    title,
    created_at: now,
    updated_at: now,
    created_by: createdBy,
    requires: ["babyai"],
    provider_preference: "babyai",
    mosh_pit_enabled: true,
    tools_enabled: false,
    messages: [],
  };
}

export function appendMessage(session: Session, message: Omit<SessionMessage, "id" | "created_at">): Session {
  const full: SessionMessage = {
    id: crypto.randomUUID(),
    created_at: new Date().toISOString(),
    ...message,
  };
  return {
    ...session,
    updated_at: full.created_at,
    messages: [...session.messages, full],
  };
}

export function serializeSession(session: Session): string {
  return JSON.stringify(session, null, 2);
}

export function parseSession(text: string): Session {
  const raw = JSON.parse(text);
  if (typeof raw !== "object" || raw === null) {
    throw new Error("invalid session: not an object");
  }
  const obj = raw as Partial<Session>;
  if (obj.format_version !== SESSION_FORMAT_VERSION) {
    throw new Error(`unsupported session format_version: ${obj.format_version}`);
  }
  if (!obj.id || !obj.title || !Array.isArray(obj.messages)) {
    throw new Error("invalid session: missing required fields");
  }
  return obj as Session;
}

export function downloadSession(session: Session): void {
  const blob = new Blob([serializeSession(session)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeTitle = session.title.replace(/[^a-z0-9-_]+/gi, "_").slice(0, 40);
  a.download = `${safeTitle}-${session.id.slice(0, 8)}.playground.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Shareable invite links
// ---------------------------------------------------------------------------
//
// The session is base64url-encoded into a `?join=` query param. Anyone
// with the URL can open the channel — same trust model as exporting the
// JSON file, just over a different transport. Email/Slack-friendly for
// small (empty or near-empty) channels; gigantic threads should still
// go via file export.
//
// URL_SIZE_LIMIT is a soft cap — browsers handle longer, but mail/chat
// platforms occasionally truncate. 8KB covers a fresh invite and a
// surprising number of mid-sized threads. Above that, suggest export.

export const URL_SIZE_LIMIT = 8 * 1024;

export interface ShareLinkResult {
  url: string;
  /** Encoded payload byte length, useful for surfacing "too big" warnings. */
  size: number;
  /** True if the encoded size crossed URL_SIZE_LIMIT — the link works but
   *  may not survive all chat/email platforms. */
  oversized: boolean;
}

export function encodeShareLink(session: Session, base: string): ShareLinkResult {
  const json = JSON.stringify(session);
  const bytes = new TextEncoder().encode(json);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const b64url = btoa(bin)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
  const url = `${base}${base.includes("?") ? "&" : "?"}join=${b64url}`;
  return { url, size: b64url.length, oversized: b64url.length > URL_SIZE_LIMIT };
}

export function decodeShareParam(b64url: string): Session {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
  const bin = atob(b64 + pad);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const json = new TextDecoder().decode(bytes);
  return parseSession(json);
}

/** Pull a `?join=` param off the current URL, if present. Returns the
 *  parsed session (null on absence/parse failure). The caller is
 *  responsible for stripping the param from the URL afterward. */
export function readJoinParamFromLocation(): Session | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("join");
  if (!raw) return null;
  try {
    return decodeShareParam(raw);
  } catch (e) {
    console.warn("[session] join param failed to decode:", e);
    return null;
  }
}

/** Remove `?join=` from the URL without a page reload. */
export function stripJoinParamFromLocation(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.delete("join");
  window.history.replaceState({}, "", url.toString());
}

export async function uploadSession(): Promise<Session | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      try {
        const text = await file.text();
        resolve(parseSession(text));
      } catch (e) {
        console.error("[session] import failed:", e);
        alert(`Couldn't load that file: ${e instanceof Error ? e.message : String(e)}`);
        resolve(null);
      }
    };
    input.click();
  });
}
